import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CloudArrowUpIcon, CheckCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { backgroundImageApi, BackgroundImage } from '@/services/backgroundImageApi';

interface BackgroundImagePickerProps {
  value: string | null;
  onChange: (backgroundImageId: string) => void;
}

// Pick an existing background image (own org or shared library) or upload a
// new one — built like AvatarLibrary.tsx's upload/library halves, but as a
// lightweight picker rather than a full management page, since this only
// needs to be embedded in the Create Video wizard's background step.
const BackgroundImagePicker: React.FC<BackgroundImagePickerProps> = ({ value, onChange }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['background-images'],
    queryFn: () => backgroundImageApi.getImages(),
    staleTime: 60_000,
  });

  const ownImages: BackgroundImage[] = data?.images?.own ?? [];
  const libraryImages: BackgroundImage[] = data?.images?.library ?? [];

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => backgroundImageApi.uploadImage(formData),
    onSuccess: (res) => {
      toast.success('Image uploaded');
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadPreview(null);
      setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['background-images'] });
      onChange(res.image._id);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Upload failed'),
  });

  const handleFileSelect = (file: File | null) => {
    setUploadFile(file);
    if (!file) {
      setUploadPreview(null);
      return;
    }
    if (!uploadName) setUploadName(file.name.replace(/\.[^/.]+$/, ''));
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview((ev.target?.result as string) || null);
    reader.readAsDataURL(file);
  };

  const handleSubmitUpload = () => {
    if (!uploadFile) return toast.error('Please select an image file');
    if (!uploadName.trim()) return toast.error('Name is required');
    const formData = new FormData();
    formData.append('image', uploadFile);
    formData.append('name', uploadName.trim());
    uploadMutation.mutate(formData);
  };

  const renderGrid = (images: BackgroundImage[], emptyLabel: string) => {
    if (images.length === 0) {
      return <p className="text-sm text-gray-400 dark:text-gray-500 py-4">{emptyLabel}</p>;
    }
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {images.map((image) => (
          <button
            key={image._id}
            type="button"
            onClick={() => onChange(image._id)}
            className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-colors ${
              value === image._id
                ? 'border-blue-500 ring-2 ring-blue-500/40'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            title={image.name}
          >
            <img
              src={backgroundImageApi.getImageSrc(image.imageUrl)}
              alt={image.name}
              className="w-full h-full object-cover"
            />
            {value === image._id && (
              <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-0.5">
                <CheckCircleIcon className="h-4 w-4 text-white" />
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1.5 py-0.5">
              <p className="text-[11px] text-white truncate">{image.name}</p>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Your images</h3>
        <button
          type="button"
          onClick={() => setShowUploadForm((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <CloudArrowUpIcon className="h-4 w-4" />
          Upload new
        </button>
      </div>

      {showUploadForm && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 w-28 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-900"
            >
              {uploadPreview ? (
                <img src={uploadPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <PhotoIcon className="h-6 w-6 text-gray-400" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
            />
            <div className="flex-1 space-y-2">
              <input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="Image name"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitUpload}
                  disabled={!uploadFile || uploadMutation.isLoading}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {uploadMutation.isLoading ? 'Uploading…' : 'Upload & select'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {renderGrid(ownImages, 'No images uploaded yet — upload one above.')}

          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Library images</h3>
          {renderGrid(libraryImages, 'No library images available.')}
        </>
      )}
    </div>
  );
};

export default BackgroundImagePicker;
